import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSystem } from '../../../hooks/useSystem';
import { observer } from 'mobx-react';
import { SearchInputWidget } from '../../../widgets/forms/SearchInputWidget';
import { EmptyWidget } from '../../../widgets/content/EmptyWidget';
import { AnimatePageWidget } from '../../../widgets/layout/AnimatePageWidget';
import { useNavigate, useParams } from 'react-router-dom';
import { ProfileModel } from '../../../core/models/ProfileModel';
import { useBufferedSearch } from '../../../hooks/useBufferedSearch';
import { AnimatedCollection, AnimatedCollectionType } from '../../../widgets/collection/AnimatedCollection';
import { ProfileListItemWidget } from '../../../widgets/controls/list/ProfileListItemWidget';
import { SelectInputWidget } from '../../../widgets/forms/SelectInputWidget';
import { GroupProfileRole } from '@foxbun/lib-goferal-types';
import { InputContainerWidget } from '../../../widgets/forms/InputContainerWidget';
import { Font } from '../../../fonts';

export const InvitePackMemberPage: React.FC = observer(() => {
  const system = useSystem();

  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState<string>(null);
  const [users, setUsers] = useState<ProfileModel[]>([]);
  let pack = system.pack_store.getModel(params.id);
  const [type, setType] = useState(GroupProfileRole.MEMBER);

  useEffect(() => {
    pack.refresh();
  }, [pack]);

  useBufferedSearch({
    search,
    cb: (search) => {
      if (search) {
        setLoading(true);
        system.user_store
          .getProfiles({
            profile_name: search
          })
          .then((p) => {
            setUsers(p);
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  });

  return (
    <AnimatePageWidget slide={false}>
      <S.Profiles>
        <S.Heading>Invite member to pack: {pack.data?.name}</S.Heading>
        <S.Controls>
          <InputContainerWidget label="Member role">
            <SelectInputWidget
              value={type}
              options={[
                {
                  key: GroupProfileRole.OWNER,
                  title: 'Owner'
                },
                {
                  key: GroupProfileRole.MEMBER,
                  title: 'Member'
                }
              ]}
              onChange={(type) => {
                setType(type as GroupProfileRole);
              }}
            />
          </InputContainerWidget>
          <S.Input
            value={search}
            placeholder="search profiles"
            loading={loading}
            onChange={(search) => {
              setSearch(search);
            }}
          />
        </S.Controls>
        {!loading && users.length === 0 ? <EmptyWidget label="No profiles found" /> : null}
        {users.length > 0 ? (
          <AnimatedCollection<ProfileModel>
            type={AnimatedCollectionType.SLIDE_IN_LEFT}
            children={users}
            generate_inner={(p) => {
              return (
                <ProfileListItemWidget
                  key={p.id.toString()}
                  profile={p}
                  action={async () => {
                    let invite = await system.dialog_store.showConfirmDialog({
                      title: 'Invite member?',
                      desc: 'The user will be invited to the pack'
                    });
                    if (!invite) {
                      return;
                    }

                    await pack.inviteMember(p, type);
                    let invite_another = await system.dialog_store.showConfirmDialog({
                      title: 'Invite another?',
                      desc: 'Invite another member to this pack'
                    });
                    if (!invite_another) {
                      navigate(-1);
                    }
                  }}
                />
              );
            }}
          />
        ) : null}
      </S.Profiles>
    </AnimatePageWidget>
  );
});

namespace S {
  export const Input = styled(SearchInputWidget)``;

  export const Controls = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 5px;
  `;

  export const Profiles = styled.div`
    padding: 10px;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  `;

  export const Heading = styled.div`
    font-size: 20px;
    color: ${(p) => p.theme.text.primary};
    font-family: ${Font.Normal};
  `;
}
